import type { CreateAgentRequest, InviteGuestRequest } from "@bundle/contracts";
import { useState } from "react";
import { Button, Checkbox, ScrollView, SegmentedControl, Sheet, Stack, Text, TextField, Toggle } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";

export type ChannelOption = { id: string; name: string };

const EXPIRY_DAYS = [0, 7, 30, 90];
const DAY_MS = 86_400_000;

function ChannelPicker({ channels, selected, onChange, testID }: { channels: ChannelOption[]; selected: string[]; onChange: (ids: string[]) => void; testID: string }) {
  return (
    <Stack gap="0.5" testID={testID}>
      {channels.map((c) => (
        <Checkbox
          key={c.id}
          label={`#${c.name}`}
          value={selected.includes(c.id)}
          onChange={(v) => onChange(v ? [...selected, c.id] : selected.filter((x) => x !== c.id))}
          testID={`${testID}-${c.id}`}
        />
      ))}
    </Stack>
  );
}

export type InviteGuestSheetProps = {
  open: boolean;
  onClose: () => void;
  channels: ChannelOption[];
  now: number;
  /** Resolves with the guest's sign-in link (shown with a copy button), if the server returned one. */
  onSubmit: (req: InviteGuestRequest) => Promise<string | undefined>;
  /** Copies text to the clipboard (platform). */
  onCopy?: (text: string) => Promise<void>;
};

/** Invite someone from another organization into selected channels, optionally with an expiry. */
export function InviteGuestSheet(p: InviteGuestSheetProps) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | undefined>(undefined);
  const [link, setLink] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && org.trim() !== "" && channelIds.length > 0;

  const submit = () => {
    if (!valid || busy) return;
    const req: InviteGuestRequest = { email: email.trim(), organization: { name: org.trim() }, channelIds };
    if (name.trim() !== "") req.name = name.trim();
    if (days > 0) req.expiresAt = p.now + days * DAY_MS;
    setBusy(true);
    setFailed(false);
    p.onSubmit(req).then(
      (url) => {
        setBusy(false);
        setSentTo(req.email);
        setLink(url);
        setCopied(false);
        setEmail("");
        setName("");
      },
      () => {
        setBusy(false);
        setFailed(true);
      },
    );
  };

  return (
    <Sheet open={p.open} onClose={p.onClose} title={t("invite.guest.title")} closeLabel={t("common.close")} testID="invite-guest-sheet">
      <Stack gap="4">
        <Text variant="footnote" color="text.secondary">
          {t("invite.guest.notice")}
        </Text>
        <TextField label={t("invite.guest.email")} value={email} onChange={setEmail} keyboard="email" autoFocus testID="invite-email" />
        <Stack direction="horizontal" align="center" gap="3">
          <TextField label={t("invite.guest.name")} value={name} onChange={setName} testID="invite-name" />
          <TextField label={t("invite.guest.organization")} value={org} onChange={setOrg} testID="invite-org" />
        </Stack>
        <Stack gap="1.5">
          <Text variant="footnote" weight="medium" color="text.secondary">
            {t("invite.guest.channels")}
          </Text>
          <ScrollView>
            <ChannelPicker channels={p.channels} selected={channelIds} onChange={setChannelIds} testID="invite-channels" />
          </ScrollView>
        </Stack>
        <Stack gap="1.5">
          <Text variant="footnote" weight="medium" color="text.secondary">
            {t("invite.guest.expiry")}
          </Text>
          <SegmentedControl
            variant="pills"
            size="sm"
            accessibilityLabel={t("invite.guest.expiry")}
            options={EXPIRY_DAYS.map((d) => ({ value: String(d), label: d === 0 ? t("invite.guest.expiry.never") : t("invite.guest.expiry.days", { count: d }) }))}
            value={String(days)}
            onChange={(d) => setDays(Number(d))}
            testID="invite-expiry"
          />
        </Stack>
        {sentTo === undefined ? null : (
          <Text variant="footnote" color="status.success">
            {t("invite.guest.sent", { email: sentTo })}
          </Text>
        )}
        {sentTo === undefined || link === undefined ? null : (
          <Stack gap="1" testID="invite-guest-link">
            <Text variant="footnote" color="text.secondary">
              {t("invite.guest.link", { email: sentTo })}
            </Text>
            <Stack direction="horizontal" gap="2" align="center">
              <Stack grow>
                <Text mono selectable variant="footnote" lines={1}>
                  {link}
                </Text>
              </Stack>
              {p.onCopy === undefined ? null : (
                <Button
                  label={copied ? t("invite.people.copied") : t("invite.people.copy")}
                  icon={copied ? "check" : "copy"}
                  size="sm"
                  onPress={() => {
                    void p.onCopy?.(link).then(() => setCopied(true));
                  }}
                  testID="invite-guest-copy"
                />
              )}
            </Stack>
          </Stack>
        )}
        {failed ? (
          <Text variant="footnote" color="status.danger">
            {t("common.error")}
          </Text>
        ) : null}
        <Stack direction="horizontal" align="center" justify="end" gap="2">
          <Button label={t("common.cancel")} variant="ghost" onPress={p.onClose} testID="invite-cancel" />
          <Button label={t("invite.guest.submit")} variant="primary" icon="mail" loading={busy} disabled={!valid} onPress={submit} testID="invite-submit" />
        </Stack>
      </Stack>
    </Sheet>
  );
}

export type CreateAgentSheetProps = {
  open: boolean;
  onClose: () => void;
  channels: ChannelOption[];
  /** Resolves with the one-time token for external agents. */
  onSubmit: (req: CreateAgentRequest) => Promise<string | undefined>;
};

/** Create an agent (restricted, sponsored by me) and add it to channels. */
export function CreateAgentSheet(p: CreateAgentSheetProps) {
  const t = useT();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hosted, setHosted] = useState(true);
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  const submit = () => {
    if (name.trim() === "" || busy) return;
    const req: CreateAgentRequest = { displayName: name.trim(), runtime: hosted ? "hosted" : "external", channelIds };
    if (description.trim() !== "") req.description = description.trim();
    setBusy(true);
    setFailed(false);
    p.onSubmit(req).then(
      (tok) => {
        setBusy(false);
        if (tok === undefined) p.onClose();
        else setToken(tok);
      },
      () => {
        setBusy(false);
        setFailed(true);
      },
    );
  };

  return (
    <Sheet open={p.open} onClose={p.onClose} title={t("agent.create.title")} closeLabel={t("common.close")} testID="create-agent-sheet">
      {token === undefined ? (
        <Stack gap="4">
          <TextField label={t("agent.create.name")} value={name} onChange={setName} autoFocus testID="agent-name" />
          <TextField label={t("agent.create.description")} value={description} onChange={setDescription} multiline testID="agent-description" />
          <Toggle label={t("agent.create.runtime")} value={hosted} onChange={setHosted} testID="agent-hosted" />
          <Stack gap="1.5">
            <Text variant="footnote" weight="medium" color="text.secondary">
              {t("invite.guest.channels")}
            </Text>
            <ChannelPicker channels={p.channels} selected={channelIds} onChange={setChannelIds} testID="agent-channels" />
          </Stack>
          {failed ? (
            <Text variant="footnote" color="status.danger">
              {t("common.error")}
            </Text>
          ) : null}
          <Stack direction="horizontal" align="center" justify="end" gap="2">
            <Button label={t("common.cancel")} variant="ghost" onPress={p.onClose} testID="agent-cancel" />
            <Button label={t("agent.create.submit")} variant="primary" icon="agent" loading={busy} disabled={name.trim() === ""} onPress={submit} testID="agent-submit" />
          </Stack>
        </Stack>
      ) : (
        <Stack gap="3">
          <Text color="text.secondary">{t("agent.create.tokenNotice")}</Text>
          <Stack padding="3" radius="md" background="bg.surface" border="border.subtle">
            <Text mono selectable variant="footnote" testID="agent-token">
              {token}
            </Text>
          </Stack>
          <Stack direction="horizontal" align="center" justify="end">
            <Button label={t("common.close")} variant="primary" onPress={p.onClose} testID="agent-done" />
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}
